// Service layer for product data interactions (e.g., Firestore)

import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  setDoc, 
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { Product, standardizeProduct } from '../utils/productUtils';

/**
 * Ruft alle Produkte eines Benutzers ab
 */
export const fetchProducts = async (userId: string): Promise<Product[]> => {
  if (!userId) {
    throw new Error('Benutzer-ID fehlt');
  }

  try {
    const productsRef = collection(db, 'products');
    const q = query(productsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const products: Product[] = [];
    querySnapshot.forEach((doc) => {
      products.push({
        id: doc.id,
        ...doc.data()
      } as Product);
    });
    
    return products;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Speichert ein Produkt in der Datenbank (neu oder aktualisiert)
 */
export const saveProduct = async (userId: string, product: Partial<Product>, productId?: string): Promise<string> => {
  if (!userId) {
    throw new Error('Benutzer-ID fehlt');
  }

  try {
    // Standardisiere das Produkt
    const standardizedProduct = standardizeProduct(product);
    
    // Erstelle eine Referenz zum Produkt (neu oder bestehend)
    let productRef;
    
    if (productId) {
      // Aktualisiere bestehendes Produkt
      productRef = doc(db, 'products', productId);
      
      // Prüfen, ob das Produkt existiert
      const docSnap = await getDoc(productRef);
      
      if (docSnap.exists()) {
        await updateDoc(productRef, standardizedProduct);
      } else {
        await setDoc(productRef, standardizedProduct);
      }
    } else {
      // Erstelle neues Produkt
      productRef = doc(collection(db, 'products'));
      await setDoc(productRef, {
        ...standardizedProduct,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    return productRef.id;
  } catch (err: any) {
    console.error('Fehler beim Speichern des Produkts:', err);
    throw new Error(`Fehler beim Speichern: ${err.message || 'Unbekannter Fehler'}`);
  }
};

/**
 * Löscht ein Produkt aus der Datenbank
 */
export const deleteProduct = async (userId: string, productId: string): Promise<void> => {
  if (!userId || !productId) {
    throw new Error('Benutzer-ID oder Produkt-ID fehlt');
  }

  try {
    await deleteDoc(doc(db, 'products', productId));
  } catch (err: any) {
    console.error('Fehler beim Löschen des Produkts:', err);
    throw new Error(`Fehler beim Löschen: ${err.message || 'Unbekannter Fehler'}`);
  }
};

/**
 * Importiert mehrere Produkte in die Datenbank
 */
export const importProducts = async (
  userId: string, 
  products: Partial<Product>[]
): Promise<{ successCount: number, updatedIds: string[], newIds: string[] }> => {
  if (!userId || !products.length) {
    throw new Error('Benutzer-ID fehlt oder keine Produkte zum Importieren');
  }

  try {
    // Erfolgreiche Importe zählen
    let successCount = 0;
    const updatedIds: string[] = [];
    const newIds: string[] = [];
    
    // Speichere jedes importierte Produkt
    for (const product of products) {
      if (!product.name) continue;
      
      // Standardisiere das Produkt
      const standardizedProduct = standardizeProduct(product);
      
      // Überprüfen, ob bereits ein Produkt mit diesem Namen existiert
      const existingQuery = query(
        collection(db, 'products'),
        where('userId', '==', userId),
        where('name', '==', standardizedProduct.name)
      );
      
      const existingDocs = await getDocs(existingQuery);
      let productId = '';
      
      if (!existingDocs.empty) {
        // Produkt existiert bereits, aktualisieren
        const existingDoc = existingDocs.docs[0];
        productId = existingDoc.id;
        await updateDoc(doc(db, 'products', productId), {
          ...standardizedProduct,
          updatedAt: serverTimestamp()
        });
        updatedIds.push(productId);
      } else {
        // Neues Produkt erstellen
        const newProductRef = doc(collection(db, 'products'));
        productId = newProductRef.id;
        await setDoc(newProductRef, {
          ...standardizedProduct,
          userId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        newIds.push(productId);
      }
      
      // Erfolgreich importiert
      successCount++;
    }
    
    return { 
      successCount, 
      updatedIds, 
      newIds 
    };
  } catch (err: any) {
    console.error('Fehler beim Import der Produkte:', err);
    throw new Error(`Fehler beim Import: ${err.message || 'Unbekannter Fehler'}`);
  }
};

// Fetch all products for a user
export async function getProducts(userId: string): Promise<Product[]> {
  try {
    const productsRef = collection(db, 'products');
    const q = query(productsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const products: Product[] = [];
    querySnapshot.forEach((doc) => {
      products.push({
        id: doc.id,
        ...doc.data()
      } as Product);
    });
    
    return products;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
}

// Create a new product
export async function createProduct(userId: string, productData: Omit<Product, 'id'>): Promise<Product> {
  try {
    // Validate required fields
    if (!productData.name || productData.name.trim() === '') {
      throw new Error('Product name is required');
    }
    
    const productsRef = collection(db, 'products');
    
    const newProductData: any = {
      ...productData,
      userId, // Add userId as field
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Ensure price and taxRate are numbers
    if (typeof newProductData.price === 'string') {
      newProductData.price = parseFloat(newProductData.price) || 0;
    }
    if (typeof newProductData.taxRate === 'string') {
      newProductData.taxRate = parseFloat(newProductData.taxRate) || 0;
    }
    
    const newProductRef = doc(productsRef);
    await setDoc(newProductRef, newProductData);
    
    return {
      id: newProductRef.id,
      ...productData
    } as Product;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
}

// Update an existing product
export async function updateProduct(userId: string, productId: string, productData: Partial<Product>): Promise<void> {
  try {
    const productRef = doc(db, 'products', productId);
    
    const updateData: any = {
      ...productData,
      userId, // Ensure userId remains set
      updatedAt: serverTimestamp()
    };
    
    // Remove id from update data if present
    delete updateData.id;
    
    // Ensure price and taxRate are numbers if being updated
    if (updateData.price !== undefined && typeof updateData.price === 'string') {
      updateData.price = parseFloat(updateData.price) || 0;
    }
    if (updateData.taxRate !== undefined && typeof updateData.taxRate === 'string') {
      updateData.taxRate = parseFloat(updateData.taxRate) || 0;
    }
    
    await updateDoc(productRef, updateData);
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
}

// Delete a product
export async function deleteProductById(userId: string, productId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'products', productId));
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
} 